import React, { memo, useMemo } from 'react';

import { MetricMonthlySummaryCard } from '../metricMonthlySummaryCard/MetricMonthlySummaryCard';
import { MetricTimeSeriesBarGraph } from '../metricTimeSeriesBarGraph/MetricTimeSeriesBarGraph';
import { isTieredScorecard, getExceedsTarget } from '../../utils/metricAttainment';

interface PropTypes {
    metrics: any[];
    dailyMetricsData: any;
    monthlyMetricHistory: any;
    scorecardVersionId: number;
    employeeNumber: string;
    fromMetricDashboard: boolean;
    selectedMetricName: string;
    selectedMetricColorIndex: number;
    onCardClick: (metricName: string, index: number) => void;
    currentMonthLabel?: string;
    historyLabel?: string;
    children?: React.ReactNode;
}

const StandardMetricsPanel = memo(({
    metrics,
    dailyMetricsData,
    monthlyMetricHistory,
    scorecardVersionId,
    employeeNumber,
    fromMetricDashboard,
    selectedMetricName,
    selectedMetricColorIndex,
    onCardClick,
    currentMonthLabel,
    historyLabel,
    children,
}: PropTypes) => {
    // Collect all rows matching the selected metric, merge their dailyMetricSummaryList
    // arrays into one date-sorted list — same pattern as the original CoachingActionPlanScoreCardDashboard
    const mergedDailyElement = useMemo(() => {
        if (!selectedMetricName) return null;
        const allMatching: any[] = dailyMetricsData?.data?.filter(
            (el: any) => el.label === selectedMetricName
        ) ?? [];
        if (allMatching.length === 0) return null;
        return {
            ...allMatching[0],
            dailyMetricSummaryList: allMatching
                .flatMap((el: any) => el.dailyMetricSummaryList ?? [])
                .sort((a: any, b: any) =>
                    new Date(b.result_date).getTime() - new Date(a.result_date).getTime()
                ),
        };
    }, [dailyMetricsData, selectedMetricName]);

    const selectedMetricData = useMemo(
        () => metrics.find((m: any) => m.label === selectedMetricName),
        [metrics, selectedMetricName]
    );

    const filteredHistoryData = useMemo(
        () => monthlyMetricHistory?.data?.filter((el: any) => el.label === selectedMetricName) ?? [],
        [monthlyMetricHistory, selectedMetricName]
    );

    return (
        <>
            <div className="col-12 p-0">
                <div className="d-flex flex-wrap justify-content-start">
                    {metrics.map((element: any, index: number) => {
                        // Explicit model switch per metric row - never inferred from whether
                        // targets/attainmentLevelId happen to be present on the row.
                        const tiered = isTieredScorecard(element?.scorecard_calculation_rule_id);
                        return (
                            <div
                                style={{ flex: '1 1 200px', minWidth: '200px', boxSizing: 'border-box', padding: '0px' }}
                                key={`${element?.label}-${index}`}
                            >
                                <MetricMonthlySummaryCard
                                    targetVal={element?.target}
                                    metricAttained={element?.metric_attained}
                                    metricNameVal={element?.label}
                                    metricTypeName={element?.metric_type_name}
                                    metricVal={element?.metric_value}
                                    metricDesc={element?.metric_desc}
                                    metricDirectionId={element?.metric_direction_id}
                                    onCardClick={onCardClick}
                                    selectedMetric={selectedMetricName}
                                    index={index}
                                    metricOmitted={element?.metric_omitted}
                                    variant={tiered ? 'tiered' : 'legacy'}
                                    attainmentRank={element?.attainment_rank}
                                    attainmentLevelLabel={element?.metric_attainment_level_label}
                                    targets={element?.targets}
                                    min={element?.min}
                                    max={element?.max}
                                />
                            </div>
                        );
                    })}
                </div>
            </div>

            {selectedMetricName && mergedDailyElement && (
                <div className="mt-0 p-0">
                    <MetricTimeSeriesBarGraph
                        metricName={mergedDailyElement.label}
                        metricId={mergedDailyElement.metric_id}
                        scorecardVersionId={scorecardVersionId}
                        employeeNumber={employeeNumber}
                        target={mergedDailyElement.target}
                        metricTypeName={mergedDailyElement.metric_type_name}
                        dailyData={mergedDailyElement.dailyMetricSummaryList}
                        metricDirectionId={mergedDailyElement.metric_direction_id}
                        calculatedDate={mergedDailyElement.calculate_date}
                        metricHistoryData={filteredHistoryData}
                        fromMetricDashboard={fromMetricDashboard}
                        metricIndex={selectedMetricColorIndex}
                        metricOmitted={selectedMetricData?.metric_omitted || false}
                        currentMonthLabel={currentMonthLabel}
                        historyLabel={historyLabel}
                        exceedsTarget={getExceedsTarget(mergedDailyElement.targets ?? selectedMetricData?.targets)}
                    />
                </div>
            )}

            {/* Caller-specific slot — e.g. AgentDiscussionsPlot */}
            {children}
        </>
    );
});

StandardMetricsPanel.displayName = 'StandardMetricsPanel';

export default StandardMetricsPanel;
